import { createObjectFromArray, removeByIndex } from '@mng/mng-ts-helpers';
import { SafeHtml } from '@angular/platform-browser';

export interface IOption<T = any, R = T> {
  name: string;
  value: T;
  info?: string | (() => string);
  htmlInfo?: SafeHtml;
  realValue?: R;
  tooltip?: string | (() => string);
}

export interface IOptionGroup<T = any, R = T> {
  name: string;
  htmlInfo?: SafeHtml;
  options: IOption<T, R>[];
}

const emptyOption = (name: string, value: unknown) => ({ name, value }) as IOption;

export function optionsAsObject<T>(options: IOption<T>[]) {
  return createObjectFromArray(options, 'value');
}

export function withEmptyOption(options: IOption[], emptyFirst = true, name = '-', value = undefined): IOption[] {
  return emptyFirst ? [emptyOption(name, value), ...options] : [...options, emptyOption(name, value)];
}

export function optionsForStrings(values: string[]): IOption[] {
  return values?.map((value) => ({ name: value, value }) as IOption) ?? [];
}

export interface IMngNumberFormat {
  allowNegativeNumber: boolean;
  allowDecimals: boolean;
}

export function findOption(options: IOption[], optionValue: any): IOption {
  return optionValue ? options.find((option: IOption) => option.value === optionValue) : null;
}

export function findOptionLabel(options: IOption[], optionValue: any): string {
  const option = findOption(options, optionValue);
  return option ? option.name : null;
}

export function filterOptionsByLabelOrInfo(options: IOption[], value: string): IOption[] {
  if (!value) {
    return options;
  }
  const findValue = value.toLowerCase();
  return options.filter((option: IOption) => getRealOptionLabel(option).toLowerCase().includes(findValue));
}

export function getRealOptionLabel(option: IOption): string {
  let realLabel: string;
  if (option.info) {
    realLabel = typeof option.info === 'string' ? option.info : option.info();
  } else {
    realLabel = option.name;
  }
  return realLabel;
}

export function getOptionForLabel(options: IOption[], label: string): IOption {
  return label ? options.find((option) => getRealOptionLabel(option) === label) : undefined;
}

export function getOptionForValue(options: IOption[], value: unknown): IOption {
  return value ? options.find((option) => isValueMatch(option, value)) : undefined;
}

export function getOptionsForValues(options: IOption[], values: unknown[]): IOption[] {
  return values ? values.map((value) => options.find((option) => isValueMatch(option, value))) : [];
}

export function getOptionsExceptValues(options: IOption[], values: unknown[]): IOption[] {
  return options.filter((option) => !values?.find((value) => isValueMatch(option, value)));
}

export function isValueMatch(option: IOption, value: unknown): boolean {
  if (typeof option.value === 'string') {
    return option.value === value;
  } else if (option.value['id']) {
    return option.value['id'] === value['id'];
  } else {
    return option.value === value;
  }
}

export function removeValueFromOptions(options: IOption[], option: IOption): IOption[] {
  const foundOptionIndex = options.findIndex((currentOption) => isValueMatch(currentOption, option.value));
  if (foundOptionIndex !== -1) {
    return removeByIndex(options, foundOptionIndex);
  }
  return options;
}

export function createOptionsFromLabels<T extends string>(labelObject: { [key in T]: string }): IOption<T>[] {
  return Object.keys(labelObject).map((key) => ({ value: key as T, name: labelObject[key as T] }) as IOption<T>);
}
